import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import {
  Search, PlayCircle, Download, Bug, Eye, Clock, CheckCircle, AlertCircle,
  RefreshCw, LayoutGrid, List, X, Copy, FileDown
} from 'lucide-react';
import { toast } from 'sonner';
import { videoApiClient } from '../services/videoApi';
import { useOrganization } from '../hooks/useOrganization';
import { canEdit, canManageBilling } from '../utils/permissions';
import { isPublicMvp } from '@/lib/featureFlags';
import {
  VideoFeedbackEditor, waitForPendingFeedbackSaves
} from '@/components/assets/VideoFeedbackEditor';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Spinner } from '@/components/ui/spinner';
import { EmptyState } from '@/components/ui/empty-state';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger
} from '@/components/ui/tooltip';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from '@/components/ui/dialog';
import {
  Pagination, PaginationContent, PaginationItem, PaginationLink,
  PaginationNext, PaginationPrevious, PaginationEllipsis
} from '@/components/ui/pagination';
import { cn } from '@/lib/utils';

const PAGE_SIZE_OPTIONS = [12, 24, 48];
const DEFAULT_PAGE_SIZE = 12;

const SORT_OPTIONS = [
  { value: 'updated_at:desc', label: 'Recently Updated' },
  { value: 'created_at:desc', label: 'Newest First' },
  { value: 'created_at:asc', label: 'Oldest First' },
];

const STATE_FILTERS = [
  { value: 'all', label: 'All Status' },
  { value: 'completed', label: 'Completed' },
  { value: 'processing', label: 'Processing' },
  { value: 'failed', label: 'Failed' },
];

const jobIdOf = (asset) => asset.job_id || asset.jobId;

const StatusBadge = ({ state }) => {
  const s = state?.toLowerCase();
  switch (s) {
    case 'completed':
      return <Badge variant="outline" className="text-emerald-600 border-emerald-200 bg-emerald-50/60"><CheckCircle className="h-3 w-3 mr-1" />Ready</Badge>;
    case 'processing': case 'running': case 'rendering': case 'generating':
      return <Badge variant="outline" className="text-blue-600 border-blue-200 bg-blue-50/60"><RefreshCw className="h-3 w-3 mr-1 animate-spin" />Processing</Badge>;
    case 'failed':
      return <Badge variant="outline" className="text-red-600 border-red-200 bg-red-50/60"><AlertCircle className="h-3 w-3 mr-1" />Failed</Badge>;
    case 'queued': case 'pending':
      return <Badge variant="outline" className="text-muted-foreground"><Clock className="h-3 w-3 mr-1" />Queued</Badge>;
    default:
      return <Badge variant="outline">{s || 'Unknown'}</Badge>;
  }
};

const formatDate = (dateStr) => {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  return date.toLocaleDateString(undefined, {
    month: 'short', day: 'numeric',
    year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
  });
};

// Location and rating, shown when the scrape found no business description.
const businessFacts = (business) => {
  const location = [business.city, business.state].filter(Boolean).join(', ') || business.address;
  const rating = business.stars
    ? `★ ${Number(business.stars).toFixed(1)}${business.review_count ? ` (${business.review_count} reviews)` : ''}`
    : null;
  return [location, rating].filter(Boolean).join(' · ');
};

const copyJobId = async (jobId) => {
  try {
    await navigator.clipboard.writeText(jobId);
    toast.success('Job ID copied');
  } catch {
    toast.error('Could not copy the job ID');
  }
};

const AssetThumbnail = ({ src, alt, onPlay, className }) => {
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);

  return (
    <div className={cn("relative bg-muted overflow-hidden shrink-0", className)}>
      {src && !failed ? (
        <>
          {!loaded && <Skeleton className="absolute inset-0 rounded-none" />}
          <img
            src={src}
            alt={alt}
            loading="lazy"
            decoding="async"
            className={cn("w-full h-full object-cover transition-opacity", loaded ? "opacity-100" : "opacity-0")}
            onLoad={() => setLoaded(true)}
            onError={() => setFailed(true)}
          />
        </>
      ) : (
        <div className="flex items-center justify-center h-full text-muted-foreground">
          <PlayCircle className="h-8 w-8" />
        </div>
      )}
      {onPlay && (
        <button
          type="button"
          aria-label={`Play ${alt}`}
          className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 hover:opacity-100 focus-visible:opacity-100 transition-opacity"
          onClick={onPlay}
        >
          <PlayCircle className="h-10 w-10 text-white" />
        </button>
      )}
    </div>
  );
};

const AssetCard = ({
  asset, onNavigate, viewMode, onPlayVideo, showEditButton, canEditFeedback, onFeedbackSaved
}) => {
  const business = asset.business || {};
  const jobId = jobIdOf(asset);
  const businessName = business.name || 'Unnamed Business';
  const videoUrl = asset.video_url || asset.assets?.video_url || (jobId ? videoApiClient.getAssetUrl(jobId) : null);
  const fullVideoUrl = videoUrl
    ? (videoUrl.startsWith('/') ? `${videoApiClient.baseUrl}${videoUrl}` : videoUrl)
    : null;
  const thumbnailUrl = asset.thumbnail_url
    ? (asset.thumbnail_url.startsWith('/') ? `${videoApiClient.baseUrl}${asset.thumbnail_url}` : asset.thumbnail_url)
    : videoApiClient.getThumbnailUrl(jobId);
  const playVideo = fullVideoUrl ? () => onPlayVideo(fullVideoUrl, businessName, jobId) : null;
  const facts = businessFacts(business);

  const isList = viewMode === 'list';

  return (
    <div
      className={cn(
        "bg-card rounded-xl border border-border overflow-hidden transition-shadow hover:shadow-md",
        isList ? "flex flex-col md:flex-row gap-4 p-3" : "flex flex-col"
      )}
    >
      <AssetThumbnail
        src={thumbnailUrl}
        alt={businessName}
        onPlay={playVideo}
        className={cn("aspect-video", isList && "w-full md:w-56 rounded-lg self-start")}
      />

      <div className={cn("flex flex-col gap-1.5 min-w-0", isList ? "flex-1" : "flex-1 p-4")}>
        <div className="flex items-start justify-between gap-2">
          <h4 className="text-sm font-semibold text-foreground truncate" title={businessName}>{businessName}</h4>
          <StatusBadge state={asset.state} />
        </div>

        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
          {asset.advertiser && (
            <Badge variant="secondary" className="text-xs">{asset.advertiser.name}</Badge>
          )}
          {(asset.completed_at || asset.created_at) && (
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {formatDate(asset.completed_at || asset.created_at)}
            </span>
          )}
          {jobId && (
            <button
              type="button"
              className="flex items-center gap-1 font-mono hover:text-foreground transition-colors"
              title={`Copy job ID ${jobId}`}
              onClick={() => copyJobId(jobId)}
            >
              {jobId.slice(0, 8)}
              <Copy className="h-3 w-3" />
            </button>
          )}
        </div>

        {business.description ? (
          <p
            className={cn("text-sm text-muted-foreground leading-snug", isList ? "line-clamp-3" : "line-clamp-2")}
            title={business.description}
          >
            {business.description}
          </p>
        ) : (
          <p className="text-xs text-muted-foreground/80 italic">
            {facts || 'No business description available.'}
          </p>
        )}

        <div className="flex gap-1.5 mt-1">
          <TooltipProvider>
            {fullVideoUrl && (
              <>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button size="xs" onClick={(e) => { e.stopPropagation(); playVideo(); }}>
                      <PlayCircle className="h-3.5 w-3.5" />
                      <span className="ml-1">Play</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Play Video</TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button size="xs" variant="outline" onClick={(e) => { e.stopPropagation(); window.open(fullVideoUrl, '_blank'); }}>
                      <Download className="h-3.5 w-3.5" />
                      <span className="ml-1">Download</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Download Video</TooltipContent>
                </Tooltip>
              </>
            )}
            {isPublicMvp() ? (
              jobId && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button size="xs" variant="outline" onClick={(e) => { e.stopPropagation(); onNavigate(jobId, 'creatives/job'); }}>
                      <Eye className="h-3.5 w-3.5" />
                      <span className="ml-1">View</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Open creative</TooltipContent>
                </Tooltip>
              )
            ) : (
              showEditButton && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button size="xs" variant="outline" onClick={(e) => { e.stopPropagation(); onNavigate(jobId, 'director'); }}>
                      <Bug className="h-3.5 w-3.5" />
                      <span className="ml-1">Edit</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>View in Director Studio</TooltipContent>
                </Tooltip>
              )
            )}
          </TooltipProvider>
        </div>

        {!isList && jobId && (
          <VideoFeedbackEditor
            jobId={jobId}
            note={asset.feedback}
            onSaved={onFeedbackSaved}
            readOnly={!canEditFeedback}
            className="mt-2"
            textareaClassName="min-h-16"
          />
        )}
      </div>

      {isList && jobId && (
        <VideoFeedbackEditor
          jobId={jobId}
          note={asset.feedback}
          onSaved={onFeedbackSaved}
          readOnly={!canEditFeedback}
          className="w-full md:w-80 lg:w-96 shrink-0"
        />
      )}
    </div>
  );
};

const SkeletonCard = ({ viewMode }) => {
  const isList = viewMode === 'list';
  return (
    <div className={cn("bg-card rounded-xl border border-border overflow-hidden", isList ? "flex flex-col md:flex-row gap-4 p-3" : "flex flex-col")}>
      <Skeleton className={cn("aspect-video", isList ? "w-full md:w-56 rounded-lg" : "w-full rounded-none")} />
      <div className={cn("space-y-2", isList ? "flex-1 py-1" : "p-4")}>
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-3 w-1/2" />
        <Skeleton className="h-3 w-full" />
        <Skeleton className="h-3 w-5/6" />
      </div>
      {isList && <Skeleton className="h-20 w-full md:w-80 lg:w-96 shrink-0" />}
    </div>
  );
};

const Assets = () => {
  const navigate = useNavigate();
  const { orgSlug } = useParams();
  const { currentOrg, currentRole } = useOrganization();
  const [searchParams, setSearchParams] = useSearchParams();

  const getOrgBasePath = () => {
    if (currentOrg) return `/org/${currentOrg.slug}`;
    if (orgSlug) return `/org/${orgSlug}`;
    return '';
  };

  const initialPage = parseInt(searchParams.get('page')) || 1;
  const initialPageSize = parseInt(searchParams.get('pageSize')) || DEFAULT_PAGE_SIZE;
  const initialState = searchParams.get('state') || '';
  const initialSort = searchParams.get('sort') || 'updated_at:desc';
  const initialSearch = searchParams.get('search') || '';
  const initialView = searchParams.get('view') || 'list';

  const [assets, setAssets] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: initialPage, pageSize: initialPageSize, total: 0, hasNext: false, hasPrev: false
  });
  const [stateFilter, setStateFilter] = useState(initialState);
  const [sortOrder, setSortOrder] = useState(initialSort);
  const [searchQuery, setSearchQuery] = useState(initialSearch);
  const [viewMode, setViewMode] = useState(initialView);
  const [isExporting, setIsExporting] = useState(false);

  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [currentVideo, setCurrentVideo] = useState({ url: null, title: '', jobId: null });
  const videoRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const listRequestRef = useRef(null);

  const canEditFeedback = canEdit(currentRole);

  const handlePlayVideo = useCallback((videoUrl, title, jobId) => {
    setCurrentVideo({ url: videoUrl, title: title || 'Video', jobId });
    setVideoModalOpen(true);
  }, []);

  const handleCloseVideoModal = useCallback(() => {
    if (videoRef.current) videoRef.current.pause();
    setVideoModalOpen(false);
    setCurrentVideo({ url: null, title: '', jobId: null });
  }, []);

  const handleFeedbackSaved = useCallback((jobId, note) => {
    setAssets(prev => prev.map(asset => (jobIdOf(asset) === jobId ? { ...asset, feedback: note } : asset)));
  }, []);

  const updateUrlParams = useCallback((updates) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value) newParams.set(key, value.toString());
      else newParams.delete(key);
    });
    setSearchParams(newParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const fetchAssets = useCallback(async (page = 1, pageSize = DEFAULT_PAGE_SIZE, state = '', sort = 'updated_at:desc') => {
    // Only the latest request may update the page; older ones are cancelled.
    listRequestRef.current?.abort();
    const request = new AbortController();
    listRequestRef.current = request;

    setIsLoading(true);
    setError(null);
    try {
      // Let in-flight feedback saves land first so the page shows them.
      await waitForPendingFeedbackSaves();
      const response = await videoApiClient.listVideoAssets({
        page, pageSize, state: state || undefined, sort, organizationId: currentOrg?.id, signal: request.signal
      });
      if (request.signal.aborted) return;
      setAssets(response.assets);
      setPagination({ page: response.page, pageSize: response.pageSize, total: response.total, hasNext: response.hasNext, hasPrev: response.hasPrev });
    } catch (err) {
      if (request.signal.aborted) return;
      console.error('Failed to fetch assets:', err);
      setError(err.message || 'Unable to load video assets');
    } finally {
      if (listRequestRef.current === request) setIsLoading(false);
    }
  }, [currentOrg?.id]);

  useEffect(() => {
    if (currentOrg?.id || !orgSlug) {
      fetchAssets(initialPage, initialPageSize, initialState, initialSort);
    }
    return () => listRequestRef.current?.abort();
  }, [currentOrg?.id]);

  const filteredAssets = useMemo(() => {
    if (!searchQuery.trim()) return assets;
    const query = searchQuery.toLowerCase();
    return assets.filter(asset => {
      const business = asset.business || {};
      return business.name?.toLowerCase().includes(query) || business.address?.toLowerCase().includes(query) || jobIdOf(asset)?.toLowerCase().includes(query);
    });
  }, [searchQuery, assets]);

  const playingAsset = currentVideo.jobId
    ? assets.find(asset => jobIdOf(asset) === currentVideo.jobId)
    : null;

  const handlePageChange = (page) => {
    setPagination(prev => ({ ...prev, page }));
    updateUrlParams({ page });
    fetchAssets(page, pagination.pageSize, stateFilter, sortOrder);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleStateFilterChange = (value) => {
    const v = value === 'all' ? '' : value;
    setStateFilter(v);
    updateUrlParams({ state: v, page: 1 });
    fetchAssets(1, pagination.pageSize, v, sortOrder);
  };

  const handleSortChange = (value) => {
    setSortOrder(value);
    updateUrlParams({ sort: value, page: 1 });
    fetchAssets(1, pagination.pageSize, stateFilter, value);
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => updateUrlParams({ search: value }), 300);
  };

  const handleViewModeChange = (value) => {
    if (value) { setViewMode(value); updateUrlParams({ view: value }); }
  };

  const handleRefresh = () => fetchAssets(pagination.page, pagination.pageSize, stateFilter, sortOrder);

  const handleExportFeedback = async () => {
    if (!currentOrg?.id) return;
    setIsExporting(true);
    try {
      await waitForPendingFeedbackSaves();
      const csv = await videoApiClient.exportVideoFeedbackCsv(currentOrg.id);
      const url = URL.createObjectURL(csv);
      const link = document.createElement('a');
      link.href = url;
      link.download = `video-feedback-${currentOrg.slug}-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      console.error('Failed to export feedback:', err);
      toast.error(err.message || 'Could not export the feedback CSV');
    } finally {
      setIsExporting(false);
    }
  };

  const totalPages = Math.ceil(pagination.total / pagination.pageSize);
  const gridClassName = cn("grid gap-4", viewMode === 'list' ? "grid-cols-1" : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4");

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className={gridClassName} aria-busy="true" aria-label="Loading videos">
          {Array.from({ length: pagination.pageSize }).map((_, i) => <SkeletonCard key={i} viewMode={viewMode} />)}
        </div>
      );
    }

    if (error) {
      return (
        <EmptyState icon={AlertCircle} title="Unable to load videos" description={error}>
          <Button onClick={handleRefresh}><RefreshCw className="h-4 w-4 mr-2" />Try Again</Button>
        </EmptyState>
      );
    }

    if (filteredAssets.length === 0) {
      if (searchQuery || stateFilter) {
        return (
          <EmptyState title="No videos match your filters" description="Try adjusting your search or filter criteria.">
            <Button variant="outline" onClick={() => {
              setSearchQuery(''); setStateFilter('');
              updateUrlParams({ search: '', state: '', page: 1 });
              fetchAssets(1, pagination.pageSize, '', sortOrder);
            }}>Clear filters</Button>
          </EmptyState>
        );
      }
      return (
        <EmptyState title="No videos yet" description="Finished creatives will appear here once generation completes.">
          <Button onClick={() => navigate(`${getOrgBasePath()}/creatives`)}>Create a Video</Button>
        </EmptyState>
      );
    }

    return (
      <div className={gridClassName}>
        {filteredAssets.map((asset) => (
          <AssetCard
            key={jobIdOf(asset)}
            asset={asset}
            onNavigate={(jobId, section) => navigate(`${getOrgBasePath()}/${section}/${jobId}`)}
            viewMode={viewMode}
            onPlayVideo={handlePlayVideo}
            showEditButton={canManageBilling(currentRole)}
            canEditFeedback={canEditFeedback}
            onFeedbackSaved={handleFeedbackSaved}
          />
        ))}
      </div>
    );
  };

  const renderPagination = () => {
    if (totalPages <= 1 || (error && !isLoading)) return null;
    const pageLinkClass = isLoading ? 'pointer-events-none opacity-50' : 'cursor-pointer';
    return (
      <div className="flex justify-center mt-8">
        <Pagination>
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                onClick={() => pagination.page > 1 && handlePageChange(pagination.page - 1)}
                className={pagination.page <= 1 ? 'pointer-events-none opacity-50' : pageLinkClass}
              />
            </PaginationItem>
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) pageNum = i + 1;
              else if (pagination.page <= 3) pageNum = i + 1;
              else if (pagination.page >= totalPages - 2) pageNum = totalPages - 4 + i;
              else pageNum = pagination.page - 2 + i;
              return (
                <PaginationItem key={pageNum}>
                  <PaginationLink
                    onClick={() => handlePageChange(pageNum)}
                    isActive={pagination.page === pageNum}
                    className={pageLinkClass}
                  >
                    {pageNum}
                  </PaginationLink>
                </PaginationItem>
              );
            })}
            <PaginationItem>
              <PaginationNext
                onClick={() => pagination.page < totalPages && handlePageChange(pagination.page + 1)}
                className={pagination.page >= totalPages ? 'pointer-events-none opacity-50' : pageLinkClass}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
        <span className="text-sm text-muted-foreground ml-4 self-center flex items-center gap-2">
          {isLoading && <Spinner size="sm" className="text-muted-foreground" />}
          {((pagination.page - 1) * pagination.pageSize) + 1}-{Math.min(pagination.page * pagination.pageSize, pagination.total)} of {pagination.total} videos
        </span>
      </div>
    );
  };

  return (
    <div className="max-w-[1400px] mx-auto px-4">
      <div className="mb-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold text-foreground">Video Assets</h1>
            {pagination.total > 0 && (
              <Badge variant="secondary">{pagination.total} video{pagination.total !== 1 ? 's' : ''}</Badge>
            )}
          </div>
          <p className="text-muted-foreground mt-1">
            Review generated videos and record what the team thinks. Feedback saves automatically.
          </p>
        </div>
        <Button variant="outline" onClick={handleExportFeedback} disabled={isExporting || !currentOrg?.id}>
          {isExporting ? <Spinner size="sm" className="text-current" /> : <FileDown className="h-4 w-4" />}
          Export feedback CSV
        </Button>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="flex flex-1 gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by business name..."
              value={searchQuery}
              onChange={handleSearchChange}
              className="pl-9"
              aria-label="Search assets by business name"
            />
          </div>
          <Select value={stateFilter || 'all'} onValueChange={handleStateFilterChange}>
            <SelectTrigger className="w-[150px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              {STATE_FILTERS.map(opt => <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={sortOrder} onValueChange={handleSortChange}>
            <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              {SORT_OPTIONS.map(opt => <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-2">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" onClick={handleRefresh} disabled={isLoading} aria-label="Refresh assets">
                  <RefreshCw className={cn("h-4 w-4", isLoading && "animate-spin")} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Refresh</TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <ToggleGroup type="single" value={viewMode} onValueChange={handleViewModeChange}>
            <ToggleGroupItem value="list" aria-label="List view"><List className="h-4 w-4" /></ToggleGroupItem>
            <ToggleGroupItem value="grid" aria-label="Grid view"><LayoutGrid className="h-4 w-4" /></ToggleGroupItem>
          </ToggleGroup>
        </div>
      </div>

      {renderContent()}
      {renderPagination()}

      <Dialog open={videoModalOpen} onOpenChange={(v) => !v && handleCloseVideoModal()}>
        <DialogContent className="max-w-[1000px] w-[90vw] sm:max-w-[1000px] max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{currentVideo.title}</DialogTitle>
          </DialogHeader>
          {currentVideo.url && (
            <div className="space-y-4">
              <video
                ref={videoRef}
                src={currentVideo.url}
                controls
                autoPlay
                className="w-full max-h-[60vh] bg-black rounded-lg"
              />
              {playingAsset && (
                <VideoFeedbackEditor
                  jobId={currentVideo.jobId}
                  note={playingAsset.feedback}
                  onSaved={handleFeedbackSaved}
                  readOnly={!canEditFeedback}
                />
              )}
              <div className="flex justify-end">
                <Button variant="outline" onClick={() => window.open(currentVideo.url, '_blank')}>
                  <Download className="h-4 w-4 mr-2" /> Download
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Assets;
